import { computed } from 'vue';

import type { NTable } from '../interface';
import type { Ref } from 'vue';

type TRecord<T> = { record: T; indent: number; index: number };

/**
 * flat tree data on expanded state
 * @param dataRef
 * @param childrenColumnNameRef
 * @param expandedKeysRef
 * @param getRowKey
 * @returns flattened data
 */
export function useFlattenRecords<T = unknown>(
  dataRef: Ref<T[]>,
  childrenColumnNameRef: Ref<string>,
  expandedKeysRef: Ref<Set<NTable.TKey>>,
  getRowKey: Ref<NTable.NRow.TGetRowKey<T>>
) {
  const arr: Ref<TRecord<T>[]> = computed(() => {
    const childrenColumnName = childrenColumnNameRef.value;
    const expandedKeys = expandedKeysRef.value;
    const data = dataRef.value;

    if (expandedKeys.size) {
      const temp: { record: T; indent: number; index: number }[] = [];

      for (let i = 0; i < data?.length; i += 1) {
        const record = data[i];

        temp.push(...flatRecord<T>(record, 0, childrenColumnName, expandedKeys, getRowKey.value, i));
      }

      return temp;
    }

    return data?.map((item, index) => ({ record: item, indent: 0, index }));
  });

  return arr;
}

function flatRecord<T>(
  record: T,
  indent: number,
  childrenColumnName: string,
  expandedKeys: Set<NTable.TKey>,
  getRowKey: NTable.NRow.TGetRowKey<T>,
  index: number
): TRecord<T>[] {
  const arr: TRecord<T>[] = [];

  arr.push({ record, indent, index });

  const key = getRowKey(record);
  const expanded = expandedKeys?.has(key);

  if (record && Array.isArray((record as any)[childrenColumnName]) && expanded) {
    for (let i = 0; i < (record as any)[childrenColumnName].length; i += 1) {
      const tempArr = flatRecord(
        (record as any)[childrenColumnName][i],
        indent + 1,
        childrenColumnName,
        expandedKeys,
        getRowKey,
        i
      );

      arr.push(...tempArr);
    }
  }

  return arr;
}
